import { useRmaStore } from '@/entities/rma';
import { useUserStore } from '@/entities/user';
import { DOUBLE_HYPHEN_BULLET, EMITTER_CONSTANTS, ERROR_ACTION } from '@/shared/config';
import { auth, emitter } from '@/shared/lib';
import { useMergeState, useUpdateEffect } from '@/shared/lib/customHooks';
import _ from 'lodash';
import { useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { facilitySelection } from '@/entities/facility';
import { getBiocareList, getPortalData, getPortalUrls } from '../../lib/sidebar';

/**
 * Supplies the sidebar with its navigation state, user identity and click handlers. Reads the current user and RMA ticket count from the stores, seeds state with the locale, Microsoft-login flag, name and roles, regenerates the allowed portal list when roles change, tracks the active pathname, refetches the RMA count on the RMA route, and memoizes the Biocare button list per roles and login origin.
 *
 * @returns The sidebar state, current user, RMA ticket count, location, Biocare button list and the ERP home, sign-out, language, popover and portal handlers.
 */
const useSidebar = () => {
  const location = useLocation();

  /**
   * Reads the current authenticated user from the user store. It selects the me field via the Zustand useUserStore selector.
   *
   * @returns The current user object from the store.
   */
  const me = useUserStore((s) => s.me);
  /**
   * Reads the current RMA ticket count from the RMA store. It selects the rmaTicketCount field via the Zustand useRmaStore selector.
   *
   * @returns The current RMA ticket count from the store.
   */
  const rmaTicketCountRedux = useRmaStore((s) => s.rmaTicketCount);

  /**
   * Fetches the RMA ticket count for a facility and stores it, selected from the RMA store via useRmaStore. Bound to the store's fetchRmaTicketCount action so it can be invoked with a facility id.
   *
   * @param facilityId - Facility whose RMA ticket count should be fetched.
   * @returns Resolves once the ticket count has been loaded into the store.
   */
  const getRmaTicketCountAction = useRmaStore((s) => s.fetchRmaTicketCount);

  const [state, setState] = useMergeState({
    pathname: '',
    selectedLanguage: auth.getLocale(),
    displayedPortals: [] as ReturnType<typeof getPortalData>,
    isLoginWithMicrosoftAccount: auth.isLoginWithMicrosoftAccount(),
    userInfo: {
      firstName: me.firstName || DOUBLE_HYPHEN_BULLET,
      lastName: me.lastName || DOUBLE_HYPHEN_BULLET,
      roles: me.roles,
    },
    isAdminOrSales: auth.isAdminOrSales(),
    openAvatarPopover: false,
  });

  /**
   * Navigates the browser to the ERP portal home page. Assigns window.location.href the url returned by the portal-url helper in ERP mode.
   *
   * @returns Nothing.
   */
  const onClickErpHomePage = () => {
    window.location.href = getPortalUrls(true);
  };

  /**
   * Signs the user out from the sidebar avatar menu. Closes the popover and emits a logout request carrying the redirect action when a redirect is wanted, otherwise the plain logout action.
   *
   * @param shouldRedirect - Whether to emit the redirect logout instead of a plain logout.
   * @returns Nothing.
   */
  const onClickSignOut = (shouldRedirect = false) => {
    setState({ openAvatarPopover: false });
    if (shouldRedirect) {
      emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.REDIRECT);
    } else {
      emitter.emit(EMITTER_CONSTANTS.LOGOUT_REQUEST, ERROR_ACTION.LOGOUT);
    }
  };

  /**
   * Switches the portal locale to the selected language. Persists the choice through the auth helper and reloads the page so the new translations apply.
   *
   * @param selectedLanguage - Locale code to save and apply.
   * @returns Nothing.
   */
  const onClickLanguage = (selectedLanguage: any) => {
    auth.saveLocale(selectedLanguage);
    window.location.reload();
  };

  /**
   * Tracks whether the sidebar avatar popover is open. Writes the incoming value into state under openAvatarPopover.
   *
   * @param newOpenAvatarPopover - The next open state of the avatar popover.
   * @returns Nothing.
   */
  const handleOpenChange = (newOpenAvatarPopover: any) => {
    setState({ openAvatarPopover: newOpenAvatarPopover });
  };

  /**
   * Opens another portal in a new browser tab, tagged as coming from the ERP portal. Appends a fromErpPortal query flag to the portal url, URI-encodes it and calls window.open.
   *
   * @param portal - Portal descriptor whose url is opened.
   * @returns Nothing.
   */
  const gotoOtherPortal = (portal: any) => {
    const urlRedirect = encodeURI(`${portal.url}?fromErpPortal=true`);
    window.open(urlRedirect);
  };

  /**
   * Builds the portal switcher entries the signed-in user is allowed to see. Reads the roles off the current user, filters the static portal data down to entries whose roles intersect them, stores the result together with the roles in state, and logs any failure.
   *
   * @returns A promise resolving once the allowed portal entries have been stored in state.
   */
  const generatePortals = async () => {
    try {
      const { roles } = me;
      const portalData = getPortalData();
      /**
       * Computes the subset of portals the current user may access by keeping only entries whose roles overlap the user's roles. It derives this from the static portalData list via a lodash filter that tests for a non-empty role intersection.
       *
       * @returns The portal entries whose roles intersect the current user's roles.
       */
      const displayedPortals = _.filter(
        portalData,
        (x) => _.intersection(x.roles, roles).length,
      ) as ReturnType<typeof getPortalData>;
      setState({ displayedPortals, userInfo: { ...state.userInfo, roles } });
    } catch (error) {
      console.error('Failed to generate portals: ', error);
    }
  };

  useEffect(() => {
    setState({ pathname: location.pathname });
  }, [location.pathname]);

  useEffect(() => {
    if (me.roles) {
      generatePortals();
    }
  }, [me.roles]);

  useEffect(() => {
    if (!auth.isLoginWithMicrosoftAccount()) {
      setState({
        userInfo: {
          firstName: me.firstName || DOUBLE_HYPHEN_BULLET,
          lastName: me.lastName || DOUBLE_HYPHEN_BULLET,
          roles: me.roles,
        },
      });
    }
  }, [me, auth.isLoginWithMicrosoftAccount()]);

  useUpdateEffect(() => {
    if (location.pathname === '/rma') {
      getRmaTicketCountAction(facilitySelection.selectedFacilityLocalStorage().value);
    }
  }, [location.pathname]);

  /**
   * Computes the memoized list of BioCare navigation buttons available to the current user. It reads the loginFrom flag from localStorage and derives the list from getBiocareList using the user's roles and whether the login origin is Mercy, recomputing when the roles change.
   *
   * @returns The BioCare button descriptors for the current user.
   */
  const biocareButtonList = useMemo(() => {
    const loginFrom = auth.getLoginFrom();
    return getBiocareList(me.roles, loginFrom === 'Mercy');
  }, [me.roles]);

  return {
    state,
    me,
    rmaTicketCountRedux,
    location,
    biocareButtonList,
    onClickErpHomePage,
    onClickSignOut,
    onClickLanguage,
    handleOpenChange,
    gotoOtherPortal,
  };
};

export { useSidebar };
